/* ============================================================
   MD · Bundle Fixed controller (Horizon theme, v2)
   - Native <select> dropdowns for variant pickers
   - Variant resolution + image swap on select change
   - Per-card price refresh + button price aggregation
   - Bundle adds all items in one /cart/add.js call
   - Opens Horizon native <cart-drawer-component>
   ============================================================ */

(function () {
  'use strict';

  /* relocateBundleSection() removido 2026-05-16: el template ya posiciona el bundle correctamente */

  const variantFeaturedImageUrl = (variant) => {
    if (!variant) return null;
    if (variant.featured_image && variant.featured_image.src) return variant.featured_image.src;
    if (variant.image && variant.image.src) return variant.image.src;
    if (typeof variant.featured_image === 'string') return variant.featured_image;
    return null;
  };

  const ensureProtocol = (url) => {
    if (!url) return url;
    if (url.startsWith('//')) return window.location.protocol + url;
    return url;
  };

  const formatCLP = (cents) =>
    '$' + Math.round((Number(cents) || 0) / 100).toLocaleString('es-CL').replace(/,/g, '.');

  async function refreshAndOpenHorizonCart(addData) {
    try {
      const eventNames = ['cart:add', 'cart:added', 'cart-add', 'cartadd'];
      eventNames.forEach((name) => {
        try {
          document.dispatchEvent(new CustomEvent(name, {
            detail: { data: addData, source: 'bundle-fixed' },
            bubbles: true,
          }));
        } catch (e) { /* ignore */ }
      });
    } catch (e) { console.warn('[md-bundle] dispatch event failed:', e); }

    try {
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return true;
      }
    } catch (e) { console.warn('[md-bundle] drawer.open failed:', e); }

    try {
      const legacyDrawer = document.querySelector('cart-drawer, #CartDrawer');
      if (legacyDrawer) {
        if (typeof legacyDrawer.open === 'function') { legacyDrawer.open(); return true; }
        if (typeof legacyDrawer.showModal === 'function') { legacyDrawer.showModal(); return true; }
        legacyDrawer.setAttribute('open', '');
        return true;
      }
    } catch (e) { /* ignore */ }

    return false;
  }

  document.querySelectorAll('[data-bf-bundle]').forEach((root) => {
    const configEl = root.querySelector('[data-bf-config]');
    const config = configEl ? JSON.parse(configEl.textContent || '{}') : {};
    const ctaBtn = root.querySelector('[data-bf-checkout]');

    const parseJSONAttr = (str, fallback) => {
      if (!str) return fallback;
      try { return JSON.parse(str); } catch { return fallback; }
    };

    const resolveVariant = (card) => {
      const variants = parseJSONAttr(card.dataset.componentVariants, null);
      const options = parseJSONAttr(card.dataset.componentOptions, null);
      if (!variants || !options) return null;
      if (variants.length === 1) return variants[0];

      const selectedByName = {};
      card.querySelectorAll('[data-bf-option-name]').forEach((sel) => {
        selectedByName[sel.dataset.bfOptionName] = sel.value;
      });
      const matched = variants.find((v) =>
        v.options.every((optVal, idx) => {
          const optName = options[idx] && options[idx].name;
          return selectedByName[optName] === optVal;
        })
      );
      return matched || variants[0];
    };

    const updateCardImage = (card, variant) => {
      const imgEl = card.querySelector('.bf__card-img');
      if (!imgEl || !variant) return;
      const newUrl = variantFeaturedImageUrl(variant);
      if (!newUrl) return;
      const currentSrc = imgEl.currentSrc || imgEl.src || '';
      if (currentSrc.split('?')[0] === ensureProtocol(newUrl).split('?')[0]) return;
      imgEl.removeAttribute('srcset');
      imgEl.src = ensureProtocol(newUrl);
    };

    const updateCardPrice = (card, variant) => {
      const priceEl = card.querySelector('[data-bf-card-price]');
      if (!priceEl || !variant) return;
      priceEl.textContent = formatCLP(variant.price);
    };

    /* Disable option values that lead only to unavailable variants given
       the other currently-selected options. */
    const refreshUnavailableOptions = (card) => {
      const variants = parseJSONAttr(card.dataset.componentVariants, null);
      const options = parseJSONAttr(card.dataset.componentOptions, null);
      if (!variants || !options) return;

      const currentByName = {};
      card.querySelectorAll('[data-bf-option-name]').forEach((sel) => {
        currentByName[sel.dataset.bfOptionName] = sel.value;
      });

      card.querySelectorAll('[data-bf-option-name]').forEach((sel) => {
        const optName = sel.dataset.bfOptionName;
        Array.from(sel.options).forEach((opt) => {
          const candidate = Object.assign({}, currentByName);
          candidate[optName] = opt.value;
          const match = variants.find((v) =>
            v.options.every((optVal, idx) => {
              const name = options[idx] && options[idx].name;
              return candidate[name] === optVal;
            })
          );
          opt.disabled = !(match && match.available);
        });
      });
    };

    const updateCardStatus = (card) => {
      refreshUnavailableOptions(card);
      const variant = resolveVariant(card);
      const statusEl = card.querySelector('[data-bf-status]');

      if (!variant) return;
      updateCardImage(card, variant);
      updateCardPrice(card, variant);

      if (statusEl) {
        statusEl.hidden = !!variant.available;
      }
    };

    const refreshButtonPrices = () => {
      /* CTA price is now driven by product.price/compare_at_price in Liquid (md-pdp-bundle-body snippet).
         Skip recalculation to keep top and bottom prices consistent. */
      return;
      const originalEl = root.querySelector('[data-bf-original-price]');
      const discountedEl = root.querySelector('[data-bf-discounted-price]');
      // originalSum = total retail (incluye gifts) — para strike-through
      // discountableSum = retail solo de items NO gift — base del cálculo
      // gifts → $0 (gratis via BXGY). Total final = discountableSum × (1 - pct%) + 0
      let originalSum = 0;
      let discountableSum = 0;
      root.querySelectorAll('[data-bf-component]').forEach((card) => {
        const v = resolveVariant(card);
        if (!v) return;
        const price = Number(v.price) || 0;
        originalSum += price;
        const isGift = card.dataset.isGift === 'true';
        if (!isGift) discountableSum += price;
      });
      const pct = Number(ctaBtn?.dataset.bfDiscountPercent || 0);
      const discountedSum = Math.round(discountableSum * (100 - pct) / 100);
      if (originalEl) originalEl.textContent = formatCLP(originalSum);
      if (discountedEl) discountedEl.textContent = formatCLP(discountedSum);
    };

    const allComponentsAvailable = () => {
      let ok = true;
      root.querySelectorAll('[data-bf-component]').forEach((card) => {
        const variant = resolveVariant(card);
        if (!variant || !variant.available) ok = false;
      });
      return ok;
    };

    const refreshCTA = () => {
      if (ctaBtn) ctaBtn.disabled = !allComponentsAvailable();
    };

    const addBundleToCart = async () => {
      if (!allComponentsAvailable()) return;
      ctaBtn.disabled = true;
      const originalHTML = ctaBtn.innerHTML;
      ctaBtn.innerHTML = '<span class="bf__cta-text">Agregando...</span>';

      let succeeded = false;
      let addData = null;
      try {
        const items = [];
        let position = 1;
        root.querySelectorAll('[data-bf-component]').forEach(function (card) {
          const v = resolveVariant(card);
          if (v) {
            items.push({
              id: v.id,
              quantity: 1,
              properties: { '_bundle': config.bundle_handle, '_bundle_position': position },
            });
          }
          position += 1;
        });

        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items,
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname,
          }),
        });
        if (!addRes.ok) throw new Error('Cart add failed: ' + addRes.status);
        addData = await addRes.json();
        console.log('[md-bundle] cart/add done, items:', (addData?.items?.length) || 0);
        succeeded = true;
      } catch (err) {
        console.error('[md-bundle] error:', err);
        alert('Hubo un problema agregando el bundle. Intenta de nuevo.');
      } finally {
        ctaBtn.disabled = false;
        ctaBtn.innerHTML = originalHTML;
      }

      if (succeeded) {
        const opened = await refreshAndOpenHorizonCart(addData);
        if (!opened) {
          console.warn('[md-bundle] cart drawer no se pudo abrir; items agregados pero sin drawer visible');
        }
      }
    };

    root.querySelectorAll('[data-bf-component]').forEach((card) => {
      card.querySelectorAll('[data-bf-option-name]').forEach((sel) => {
        sel.addEventListener('change', () => {
          updateCardStatus(card);
          refreshCTA();
          refreshButtonPrices();
        });
      });
      updateCardStatus(card);
    });

    if (ctaBtn) ctaBtn.addEventListener('click', addBundleToCart);
    refreshCTA();
    refreshButtonPrices();
  });
})();
